import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import storageManager from '../../utils/storageManager';

const initialState = {
  user: storageManager.getUser(),
  token: storageManager.getToken(),
  isAuthenticated: !!storageManager.getToken(),
  loading: false,
  error: null,
};

export const mainLogin = createAsyncThunk(
  'auth/mainLogin',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await authService.mainLogin(credentials);
      const { user, token, refreshToken } = data;

      storageManager.setToken(token);
      storageManager.setUser(user);
      if (refreshToken) {
        storageManager.setRefreshToken(refreshToken);
      }

      return { user, token };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await authService.login(credentials);
      const { user, token, refreshToken } = data;

      storageManager.setToken(token);
      storageManager.setUser(user);
      if (refreshToken) {
        storageManager.setRefreshToken(refreshToken);
      }

      return { user, token };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  }
);

export const playerLogin = createAsyncThunk(
  'auth/playerLogin',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await authService.playerLogin(formData);

      // Pending payment: profile claimed, redirect to Outseta checkout (no token yet)
      if (data.registrationStatus === 'pending_payment' || data.outsetaCheckoutUrl) {
        return { pendingPayment: true, outsetaCheckoutUrl: data.outsetaCheckoutUrl };
      }

      const { user, token, refreshToken } = data;

      storageManager.setToken(token);
      storageManager.setUser(user);
      if (refreshToken) {
        storageManager.setRefreshToken(refreshToken);
      }

      return { user, token };
    } catch (error) {
      // Backend may return pending_payment as a non-2xx status — still redirect to checkout
      const errData = error.response?.data;
      if (errData?.registrationStatus === 'pending_payment' || errData?.outsetaCheckoutUrl) {
        return { pendingPayment: true, outsetaCheckoutUrl: errData.outsetaCheckoutUrl };
      }
      return rejectWithValue(
        errData?.message || 'Player login failed. Please try again.'
      );
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const data = await authService.register(userData);
      const { user, token, refreshToken } = data;

      storageManager.setToken(token);
      storageManager.setUser(user);
      if (refreshToken) {
        storageManager.setRefreshToken(refreshToken);
      }

      return { user, token };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Registration failed. Please try again.'
      );
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      storageManager.clearAll();
      return null;
    } catch (error) {
      storageManager.clearAll();
      return rejectWithValue(
        error.response?.data?.message || 'Logout failed.'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      storageManager.setUser(action.payload);
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      storageManager.clearAll();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(mainLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mainLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(mainLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(playerLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(playerLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Pending payment: stay unauthenticated until Outseta payment completes
        if (action.payload.pendingPayment) return;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(playerLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { clearError, setUser, clearAuth } = authSlice.actions;
export default authSlice.reducer;
